import type { CSSProperties } from 'react';
import { neonVar } from '@/utils/neon-utils';
import {
  gradeCreditRangesTableWrapperStyle,
  gradeCreditRangesTableTableStyle,
  gradeCreditRangesTableHeaderCellStyle,
  gradeCreditRangesTableBodyCellStyle,
  gradeCreditRangesTableGradeColStyle,
  gradeCreditRangesTableCreditsColStyle,
  gradeCreditRangesTableInputStyle,
  gradeCreditRangesTableValidationSummaryStyle,
} from '@/components/GradeCreditRangesTable/grade-credit-ranges-table.styles';

const fontFamily = neonVar('font-family', 'Roboto, Arial, Helvetica, sans-serif');
const textColor = 'var(--Text-Color-Dark, #333333)';

/** Page-level: fill layout main area; column flex so card grows */
export const pageWrapperStyle: CSSProperties = {
  width: '100%',
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  color: textColor,
  fontFamily,
};

/** Breadcrumb + title block: do not shrink */
export const pageHeaderStyle: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

/** White card: takes remaining space; inner tab panel scrolls */
export const contentCardStyle: CSSProperties = {
  width: '100%',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  backgroundColor: '#ffffff',
  color: textColor,
  fontFamily,
  borderRadius: '8px',
  padding: '24px',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
};

/** Page title inside card: 24px, bold, same color */
export const titleStyle: CSSProperties = {
  margin: 0,
  fontFamily,
  fontSize: '24px',
  fontWeight: 700,
  lineHeight: '32px',
  color: textColor,
};

export const paragraphStyle = (fontSize: string, lineHeight: string): CSSProperties => ({
  fontFamily,
  fontSize,
  fontWeight: 400,
  lineHeight,
  letterSpacing: 'normal',
  margin: 0,
  maxWidth: 700,
  color: textColor,
});

/** Shared body text for panels and tabs — Roboto 400 14px / 20px per design spec. */
export const bodyTextStyle: CSSProperties = paragraphStyle(
  neonVar('font-size-2', '14px'),
  neonVar('line-height-2', '20px')
);

/** Modal body text — Roboto 400 14px/20px matching Typography/$font-size-2/14px regular.
 *  Applied to <div> wrappers (not <p>) to avoid Neon dialog's internal p-tag font-size resets. */
export const modalBodyTextStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: 'normal',
  color: textColor,
  margin: 0,
  display: 'block',
};

/** Tab content area: fills card, scrolls when content is long */
export const tabPanelStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  marginTop: '20px',
  overflow: 'auto',
  overflowX: 'hidden',
};

/** Inner tab content: ensures content takes space so tab panel has height */
export const tabContentBlockStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
  width: '100%',
  minHeight: 'min-content',
  color: 'inherit',
};

/** Section headings — Roboto 500 16px / 24px per design spec. */
export const sectionHeadingStyle: CSSProperties = {
  fontFamily,
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: 'normal',
  margin: 0,
  color: textColor,
};

export const sectionBlockStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

/** Horizontal rule divider between sections */
export const sectionDividerStyle: CSSProperties = {
  border: 'none',
  borderTop: '1px solid #E0E0E0',
  margin: 0,
};

/** Nested sub-block within a section (e.g. links list, document upload) — tighter 12px gap */
export const subsectionBlockStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

/** Bullet list in Academic tab (e.g. Credit recovery) */
export const bulletListStyle: CSSProperties = {
  fontFamily,
  fontSize: '14px',
  lineHeight: '20px',
  margin: 0,
  paddingLeft: '20px',
  color: textColor,
  fontWeight: 400,
};

export const bulletListItemStyle: CSSProperties = {
  marginBottom: '4px',
};

/** Centered block for loading message or error state inside the tab panel */
export const configStatusContentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  minHeight: 120,
  gap: '8px',
};

/** Buttons section: full-width footer at layout level; white background with top border */
export const configurationsActionBarStyle: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '8px',
  backgroundColor: '#ffffff',
  borderTop: '1px solid #E0E0E0',
  padding: '12px 24px',
  marginTop: '38px',
  marginBottom: '16px',
  boxSizing: 'border-box',
};

/** Right-side panel overlay (full page) */
export const panelOverlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  zIndex: 9000,
  boxSizing: 'border-box',
};

/** Right-side panel: full height, slides from right; wide enough for title on one line */
export const panelContainerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  width: '768px',
  maxWidth: '100%',
  height: '100vh',
  backgroundColor: '#ffffff',
  boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.12)',
  zIndex: 9001,
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  fontFamily,
  color: textColor,
};

export const panelHeaderStyle: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '24px 72px 16px 24px',
  borderBottom: '1px solid #E0E0E0',
  boxSizing: 'border-box',
};

export const panelTitleStyle: CSSProperties = {
  margin: 0,
  fontFamily,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '32px',
  height: '32px',
  color: textColor,
  flex: 1,
  minWidth: 0,
  maxWidth: '688px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const panelCloseButtonStyle: CSSProperties = {
  flexShrink: 0,
  flexGrow: 0,
  width: '48px',
  height: '32px',
  padding: 0,
  marginLeft: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '24px',
  lineHeight: 1,
  color: textColor,
  opacity: 1,
  boxSizing: 'border-box',
};

export const panelBodyStyle: CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: '24px 56px 24px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  boxSizing: 'border-box',
};

/** Shared with {@link GradeCreditRangesTable} (single source of truth). */
export const panelTableWrapperStyle = gradeCreditRangesTableWrapperStyle;
export const panelTableStyle = gradeCreditRangesTableTableStyle;
export const panelTableHeaderStyle = gradeCreditRangesTableHeaderCellStyle;
export const panelTableCellStyle = gradeCreditRangesTableBodyCellStyle;
export const panelTableGradeColStyle = gradeCreditRangesTableGradeColStyle;
export const panelTableCreditsColStyle = gradeCreditRangesTableCreditsColStyle;
export const panelInputStyle = gradeCreditRangesTableInputStyle;
export const panelValidationErrorStyle = gradeCreditRangesTableValidationSummaryStyle;

/** Credit deficiency panel: second column (Minimum cumulative credits earned) sums with 100px grade = 600px */
export const panelTableDeficiencyMinColStyle: CSSProperties = {
  width: '500px',
};

export const panelFooterStyle: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '8px',
  padding: '16px 56px 32px 24px',
  borderTop: '1px solid #E0E0E0',
  backgroundColor: '#ffffff',
  boxSizing: 'border-box',
};

/** Read-only table on academic tab (grade credit alerts / credit deficiency) */
export const sectionTableWrapperStyle: CSSProperties = {
  width: '600px',
  maxWidth: '100%',
  overflow: 'hidden',
};

export const sectionTableStyle: CSSProperties = {
  width: '100%',
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  fontFamily,
  fontSize: '14px',
  boxSizing: 'border-box',
};

export const sectionTableHeaderStyle: CSSProperties = {
  textAlign: 'left',
  padding: '12px 8px',
  borderBottom: '2px solid #E0E0E0',
  fontWeight: 600,
  color: textColor,
  boxSizing: 'border-box',
};

export const sectionTableCellStyle: CSSProperties = {
  padding: '8px',
  borderBottom: '1px solid #E0E0E0',
  verticalAlign: 'middle',
  boxSizing: 'border-box',
  color: textColor,
};

/** Row for Edit / Remove credit alerts buttons (same style as Set up credit alerts) */
export const creditAlertsButtonRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
};
